import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  QrCode,
  Store,
  Discount,
  LocationOn,
  Smartphone,
  Download,
  CheckCircle,
  Star,
  Verified,
  LocalOffer,
  People,
  TrendingUp,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import BackToTop from '../components/BackToTop';
import SEO from '../components/SEO';
import playStoreBadge from '../assets/play-store-badge.svg';
import appStoreBadge from '../assets/app-store-badge.svg';

const Dashboard = () => {
  const appFeatures = [
    {
      icon: <QrCode sx={{ fontSize: 50, color: '#e3662d' }} />,
      title: 'QR Code Verification',
      description: 'Simple QR code scanning to verify your 10D membership and unlock exclusive discounts at partner stores.',
    },
    {
      icon: <Store sx={{ fontSize: 50, color: '#e3662d' }} />,
      title: 'Discover Local Businesses',
      description: 'Browse through hundreds of verified partner businesses in your city. Find the best deals near you.',
    },
    {
      icon: <Discount sx={{ fontSize: 50, color: '#e3662d' }} />,
      title: 'Exclusive Member Discounts',
      description: 'Enjoy special pricing and offers only available to verified 10D members. Save on every purchase.',
    },
    {
      icon: <LocationOn sx={{ fontSize: 50, color: '#e3662d' }} />,
      title: 'Location-Based Search',
      description: 'Find partner businesses near you with our smart location-based search feature.',
    },
    {
      icon: <Verified sx={{ fontSize: 50, color: '#e3662d' }} />,
      title: 'Verified Partners',
      description: 'All businesses are verified and trusted partners of 10D Stores. Shop with confidence.',
    },
    {
      icon: <LocalOffer sx={{ fontSize: 50, color: '#e3662d' }} />,
      title: 'Special Offers',
      description: 'Get notified about flash sales, seasonal discounts, and exclusive member-only promotions.',
    },
  ];

  const howItWorksSteps = [
    {
      label: 'Download the App',
      description: 'Download the 10D Stores app from Google Play Store or Apple App Store. It\'s free to download!',
      icon: <Download sx={{ fontSize: 40, color: '#5a9b9a' }} />,
    },
    {
      label: 'Sign Up & Subscribe',
      description: 'Create your account and choose a subscription plan that suits you. Plans start from just ₹99/month.',
      icon: <People sx={{ fontSize: 40, color: '#5a9b9a' }} />,
    },
    {
      label: 'Explore Partner Businesses',
      description: 'Browse through our directory of partner businesses. Filter by category, location, or search for specific stores.',
      icon: <Store sx={{ fontSize: 40, color: '#5a9b9a' }} />,
    },
    {
      label: 'Visit & Scan QR Code',
      description: 'Visit any partner business, open the app, and scan your unique QR code at the checkout counter.',
      icon: <QrCode sx={{ fontSize: 40, color: '#5a9b9a' }} />,
    },
    {
      label: 'Enjoy Exclusive Discounts',
      description: 'Get instant discounts on your purchase. No coupons needed - your QR code is your membership card!',
      icon: <CheckCircle sx={{ fontSize: 40, color: '#5a9b9a' }} />,
    },
  ];

  const benefits = [
    {
      icon: <Star sx={{ fontSize: 40, color: '#e3662d' }} />,
      title: 'Up to 30% Off',
      description: 'Save big on every purchase with exclusive member discounts',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: '#e3662d' }} />,
      title: '1000+ Partners',
      description: 'Access to verified businesses across major Indian cities',
    },
    {
      icon: <Smartphone sx={{ fontSize: 40, color: '#e3662d' }} />,
      title: 'Easy to Use',
      description: 'Simple QR code scanning - no complicated processes',
    },
    {
      icon: <Verified sx={{ fontSize: 40, color: '#e3662d' }} />,
      title: 'Trusted Platform',
      description: 'All businesses are verified and trusted partners',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC' }}>
      <SEO
        title="10D Stores App - Features, How It Works & Download"
        description="Discover the 10D Stores mobile app features, learn how it works, and download it today. Get exclusive discounts at partner businesses across India."
        canonical="https://10dstores.in/app"
      />
      <BackToTop />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #e3662d 0%, #f17a4a 100%)',
          py: 12,
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h1" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
            The 10D Stores App
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.95 }}>
            Your gateway to exclusive discounts at local businesses across India
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 6 }}>
            Download now and start saving on every purchase
          </Typography>
        </Container>
      </Box>

      {/* Download Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Paper
          elevation={8}
          sx={{
            p: 6,
            borderRadius: 4,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
          }}
        >
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Download the App
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
            Available on both Android and iOS
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box
              component="img"
              src={playStoreBadge}
              alt="Get it on Google Play"
              sx={{
                height: 60,
                width: 'auto',
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'scale(1.05)' },
              }}
            />
            <Box
              component="img"
              src={appStoreBadge}
              alt="Download on the App Store"
              sx={{
                height: 60,
                width: 'auto',
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'scale(1.05)' },
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ mt: 4, color: 'text.secondary', fontStyle: 'italic' }}>
            * App download links will be available soon
          </Typography>
        </Paper>
      </Container>

      {/* How It Works Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 6 }}>
            How It Works
          </Typography>
          <Stepper orientation="vertical" sx={{ maxWidth: 800, mx: 'auto' }}>
            {howItWorksSteps.map((step, index) => (
              <Step key={step.label} active={true} completed={true}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: '#5a9b9a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      {step.icon}
                    </Box>
                  )}
                >
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', color: 'text.secondary' }}>
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      {/* App Features Section */}
      <Box sx={{ backgroundColor: '#F8FAFC', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 6 }}>
            App Features
          </Typography>
          <Grid container spacing={4}>
            {appFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={4}
                  sx={{
                    height: '100%',
                    p: 3,
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8,
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 6 }}>
            Why Choose 10D Stores?
          </Typography>
          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>{benefit.icon}</Box>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </Box>
        </Grid>
      ))}
    </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #5a9b9a 0%, #7ab3b2 100%)',
          py: 8,
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
            Ready to Start Saving?
            </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
            Download the 10D Stores app today and unlock exclusive discounts at partner businesses near you.
            </Typography>
          <Button
            component={Link}
            to="/customers"
            variant="contained"
            size="large"
            sx={{
              backgroundColor: '#e3662d',
              color: 'white',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              px: 6,
              py: 2,
              borderRadius: '25px',
              '&:hover': {
                backgroundColor: '#d55a1f',
                transform: 'translateY(-2px)',
              },
            }}
          >
            Learn More About Membership
          </Button>
        </Container>
            </Box>
        </Box>
  );
};

export default Dashboard;
